<template>
	<div class="vstack">
		<div class="hstack gap-3 justify-content-between align-items-center">
			<div class="vstack lh-sm">
				<h5>{{ title }}</h5>
				<div class="text-xs text-muted">{{ dateTitle }}</div>
			</div>
		</div>
		<div class="hstack gap-2 mt-2">
			<LayoutBlockStat title="Venta total" v-tooltip:top="`${totalSaleNet} sin impuestos`">
				{{ totalSale }}
			</LayoutBlockStat>
			<LayoutBlockStat title="Venta media" v-tooltip:top="`${averageSaleNet} sin impuestos`">
				{{ averageSale }}
			</LayoutBlockStat>
			<LayoutBlockStat title="Prod. medio" v-tooltip:top="`${quantityProduct} ${(quantityProduct == 1) ? 'producto' : 'productos'} en ${quantitySale} ${(quantitySale == 1) ? 'venta' : 'ventas'}`">
				{{ paTotal }}
			</LayoutBlockStat>
		</div>
		<div class="mx-n1">
			<Responsive class="w-full">
				<template #main="{ width }">
					<Chart :size="{ width, height: 300 }" :margin="{ left: 5, top: 15, right: 5, bottom: 5 }" :data="data" :axis="axis">
						<template #layers>
							<Grid strokeDasharray="1,0" :hideX="true" :hideY="true" />
							<Line :dataKeys="['name', 'total' ]" :lineStyle="{ stroke: '#1b73e8', strokeWidth: 3 }" :dotStyle="{ fill: '#1b73e8', strokeWidth: 2 }" type="monotone" />
						</template>
						<template #widgets>
							<Tooltip color="#0f172a" :config="tooltipConfig" />
						</template>
					</Chart>
				</template>
			</Responsive>
		</div>
	</div>
</template>

<script>
	import { ref, computed } from 'vue';
	import _ from 'lodash';
	import moment from 'moment';
	import { Responsive, Chart, Grid, Line, Tooltip } from 'vue3-charts';
	import { formatBetween, getDateRange } from '@/composables/date';

	export default {
		components: { Responsive, Chart, Grid, Line, Tooltip },
		props: {
			title: String,
			filters: Object,
			totals: Object
		},
		setup(props) {
			const dateTitle = formatBetween(props.filters.start_date, props.filters.end_date);
			const dateArray = getDateRange(props.filters.start_date, props.filters.end_date);
			const data = computed(() => {
				let detail = [];

				_.forEach(dateArray, (index) => {
					let info = {
						name: index
					};

					if(index in props.totals) {
						info.total = props.totals[index]?.total;
						info.total_net = props.totals[index]?.total_net;
						info.quantity = props.totals[index]?.quantity;
						info.products_quantity = props.totals[index]?.products_quantity;
						info.pa = props.totals[index]?.products_quantity / props.totals[index]?.quantity;
					}

					detail.push(info);
				});

				return detail;
			});

			const tooltipConfig = computed(() => {
				let detail = [];

				detail['name'] = { label: 'Fecha' };
				detail['total'] = { label: 'Venta total', format: ',.2f' };
				detail['total_net'] = { label: 'Sin impuestos', format: ',.2f' };
				detail['quantity'] = { label: 'Cantidad', format: ',.0f' };
				detail['products_quantity'] = { label: 'Productos', format: ',.0f' };
				detail['pa'] = { label: 'PA', format: ',.1f' };

				return detail;
			});

			const totalSale = computed(() => {
				let data = 0;

				_.forEach(props.totals, (item) => {
					data+= item.total;
				});

				return data.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
			});

			const totalSaleNet = computed(() => {
				let data = 0;

				_.forEach(props.totals, (item) => {
					data+= item.total_net;
				});

				return data.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
			});

			const averageSale = computed(() => {
				let total = 0,
					quantity = 0;

				_.forEach(props.totals, (item) => {
					total+= item.total;
					quantity+= item.quantity;
				});

				let data = (quantity > 0) ? (total / quantity) : 0;
				return data.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
			});

			const averageSaleNet = computed(() => {
				let total = 0,
					quantity = 0;

				_.forEach(props.totals, (item) => {
					total+= item.total_net;
					quantity+= item.quantity;
				});

				let data = (quantity > 0) ? (total / quantity) : 0;
				return data.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
			});

			const quantitySale = computed(() => {
				let data = 0;

				_.forEach(props.totals, (item) => {
					data+= item.quantity;
				});

				return data.toLocaleString(undefined);
			});

			const quantityProduct = computed(() => {
				let data = 0;

				_.forEach(props.totals, (item) => {
					data+= item.products_quantity;
				});

				return data.toLocaleString(undefined);
			});

			const paTotal = computed(() => {
				let products_quantity = 0,
					quantity = 0;

				_.forEach(props.totals, (item) => {
					products_quantity+= item.products_quantity;
					quantity+= item.quantity;
				});

				return ((quantity > 0) ? (products_quantity / quantity) : 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
			});

			const xAxisDivisor = computed(() => {
				let max = 8,
					length = dateArray.length;

				if(length <= max) return 1;
				else return Math.ceil(length / max);
			});

			const axis = ref({
				primary: {
					type: 'band',
					format: (val, index) => {
						return (index % xAxisDivisor.value === 0) ? moment(val).format('DD/MM') : null;
					}
				},
				secondary: {
					type: 'linear',
					ticks: 3,
					format: (val, index) => {
						return (index % 2 === 0) ? val.toLocaleString() : null;
					}
				}
			});

			return { dateTitle, dateArray, data, tooltipConfig, totalSale, totalSaleNet, averageSale, averageSaleNet, quantitySale, quantityProduct, paTotal, axis }
		}
	}
</script>